// src/components/mdx/client/visualizations/ChartVisibilityBoundary.client.tsx

'use client'

/**
 * ChartVisibilityBoundary
 * -----------------------
 *
 * Wraps expensive client-only visualizations with an IntersectionObserver so the
 * Recharts runtime and data generation helpers are only executed after the chart
 * scrolls near the viewport. Blog posts often render multiple interactive charts;
 * without deferring their mount we pay the cost of generating thousands of data
 * points during the first paint, which causes measurable hydration jank on both
 * desktop and mid-range mobile devices.
 */
import type { HTMLAttributes, ReactNode } from 'react'
import { memo, useMemo } from 'react'

import { cn } from '~/lib/utils'
import { useVisibilityObserver } from '~/lib/hooks/useVisibilityObserver'

interface RenderState {
  /** Whether the chart has ever intersected. Once true we mount permanently. */
  hasEntered: boolean
  /** Whether the observer currently sees the chart (used to pause animations). */
  isIntersecting: boolean
}

type ChartVisibilityBoundaryProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  /** Optional custom skeleton rendered before the chart enters the viewport. */
  skeleton?: ReactNode
  /** IntersectionObserver root margin. Defaults to eager preloading above fold. */
  rootMargin?: string
  /** Intersection threshold. */
  threshold?: number | number[]
  /** Render prop that receives the visibility state and returns the chart body. */
  children: (state: RenderState) => ReactNode
}

/**
 * Simple shimmering placeholder that mimics the outline of the chart card.
 */
const DefaultChartSkeleton = memo(function DefaultChartSkeleton() {
  return (
    <div className="animate-pulse rounded-3xl border border-white/10 bg-gradient-to-br from-gray-900 to-gray-950 p-6 sm:p-8">
      <div className="mb-6 h-6 w-3/4 rounded bg-white/5" />
      <div className="mb-4 h-4 w-2/3 rounded bg-white/5" />
      <div className="h-64 rounded-2xl border border-dashed border-white/10 bg-white/5" />
    </div>
  )
})

/**
 * Defers rendering of the chart body until the container intersects the viewport.
 */
export function ChartVisibilityBoundary({
  skeleton,
  rootMargin = '320px 0px 160px',
  threshold = 0.1,
  className,
  children,
  ...rest
}: ChartVisibilityBoundaryProps): JSX.Element {
  const { ref, state } = useVisibilityObserver<HTMLDivElement>({
    rootMargin,
    threshold,
    once: true
  })

  const skeletonNode = useMemo(() => skeleton ?? <DefaultChartSkeleton />, [skeleton])
  const shouldRenderChart = state.hasEntered

  return (
    <div ref={ref} className={cn('mx-auto w-full max-w-4xl p-2 sm:p-6', className)} {...rest}>
      {shouldRenderChart ? children(state) : skeletonNode}
    </div>
  )
}

export type { RenderState as ChartVisibilityRenderState }